"use client";

import { useMemo } from "react";
import { useAccount } from "wagmi";
import { $api } from "@/lib/api-client";

/**
 * Per-candidate vote totals for the current user in a given cycle.
 *
 * Backed by GET /election/votes — backend reads its own dao_vote table,
 * populated by useVoteSubmit posting to POST /election/votes after each
 * successful vote tx. Authorization is signature-based (no RPC in backend).
 *
 * Election.sol exposes only `candidates[candidate][cycle]` (total per
 * candidate) and `voterStake[user]` (total per voter), and emits no events,
 * so per-voter-per-candidate state cannot be derived from chain directly.
 *
 * Returns:
 *   - amounts: Map<lowercased-candidate-address, bigint-amount-wei>
 *   - getAmount(addr): convenience lookup, returns 0n if no vote on record
 */
export function useUserVotesForCycle(cycle: number | undefined) {
  const { address: userAddress, isConnected } = useAccount();

  const query = $api.useQuery(
    "get",
    "/election/votes",
    {
      params: { query: { cycle: cycle ?? 0 } },
    },
    {
      enabled: isConnected && cycle !== undefined,
    },
  );

  const amounts = useMemo(() => {
    const map = new Map<string, bigint>();
    const rows = query.data?.data ?? [];
    // Schema is wrapped at the controller level via WrappedApiResponseDto
    // (see src/election/decorators/election-api.decorators.ts on the backend),
    // so `query.data.data` is the typed array — no defensive narrowing needed.
    for (const row of rows) {
      try {
        map.set(row.candidate_address.toLowerCase(), BigInt(row.total_amount));
      } catch {
        // Skip rows whose total_amount isn't a valid uint string.
      }
    }
    return map;
  }, [query.data]);

  const getAmount = (candidateAddress: string): bigint => {
    if (userAddress === undefined) return 0n;
    return amounts.get(candidateAddress.toLowerCase()) ?? 0n;
  };

  return {
    amounts,
    getAmount,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
